import { combineReducers } from 'redux';
import { combineActions, handleActions } from 'redux-actions';
import moment from 'utils/cops-moment';
import {
  SET_ACTIVE_SERVICE_CENTER,
} from '../../../header/service-center-toggle/containers/constants';
import {
  PLANNING_UPDATE_ORDER_SOCKET_EVENTS_SUCCESS,
} from '../../../order/details/containers/details-constants';
import { RESET_NOTIFICATION } from '../../../planning-board/containers/planning-board-constants';
import { defaultGanttView, fseDefaultAutoSort, urlSigns } from '../../../utils/app-configs';
import { generatePreviewId, sortCOPSModelToParam } from '../../../utils/helpers';
import {
  ALERT_DISCARD_SUCCESS,
  ALERT_SNOOZE_SUCCESS,
} from '../../alerts/containers/alert-constants';
import {
  DISCARD_LOCK_SUCCESS,
  UNLOCK_LOCK_SUCCESS,
} from '../../publish-steps/containers/publish-steps-constants';
import {
  AUTO_PLANNING_ACTION,
  AUTO_PLANNING_ACTION_FAILURE,
  AUTO_PLANNING_ACTION_SUCCESS,
  CREATE_AUTO_ORDERS,
  CREATE_AUTO_ORDERS_FAILURE,
  CREATE_AUTO_ORDERS_SUCCESS,
  PUBLISH_TO_SAP_QUICK_ACTION_SUCCESS,
} from '../../toolbar/containers/constants';
import { blockConfig, ItemTypes } from '../config';
import { viewIds } from '../config/view';
import generatePreview from '../utils/generate-preview';
import * as actionTypes from './gantt-constants';

export const lanesInfo = handleActions(
  {
    [actionTypes.SET_LANES_INFO_LOCALLY]: (state, { payload }) => payload?.map(l => ({
      ...l,
      preventRefresh: true, // prevent MW refresh
    })),
    [combineActions(
      actionTypes.RESET_BOARD_DATA,
      actionTypes.OPEN_PLANNING_AND_VIEW,
    )]: () => null,
    [combineActions(
      actionTypes.CREATE_GANTT_VIEW_SUCCESS,
    )]: state => state?.map(l => ({
      ...l,
      preventRefresh: false,
    })),
    [actionTypes.SET_LANES_INFO]: (state, { payload }) => payload,
    [actionTypes.SET_LANE_INFO]: (state, { payload }) =>
      state?.map((laneInfo) => {
        const { id, totalLines } = payload;
        if (id === laneInfo.id) {
          return {
            ...laneInfo,
            totalLines,
          };
        }
        return laneInfo;
      }),
    [actionTypes.SET_LANE_INFO_IS_DRAGGING_OVER]: (state, { payload }) =>
      state?.map((laneInfo) => {
        const { id, isDraggingOver } = payload;
        if (id === laneInfo.id) {
          return {
            ...laneInfo,
            isDraggingOver,
          };
        }
        return laneInfo;
      }),
  },
  null,
);

export const lanesData = handleActions(
  {
    [actionTypes.CLEAR_LANES_DATA]: state => state?.map(lane => ({
      ...lane,
      blocks: [],
      unavailability: [],
    })),
    [actionTypes.FETCH_VIEW_DATA]: (state, { payload }) =>
      // if we only want to refetch a subset of the lanes, we should not clear the data
      payload.refetchSubSet ? state : [],
    [actionTypes.SET_LANES_DATA]: (state, { payload }) => payload,
    [actionTypes.SET_LANE_DATA]: (state, { payload }) => state?.map((lane) => {
      const payloadLane = payload?.find(l => lane?.id === l?.id);
      return payloadLane || lane;
    }),
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA_SUCCESS,
      actionTypes.RECALCULATE_TRAVEL_TIME_SUCCESS,
    )]: (state, { payload }) => {
      const {
        id,
        laneId,
        start,
        duration,
        subBlocks,
        line1,
        badges,
        actions,
        isDraft,
        isAssignedPlanner,
        showAs,
        lock,
        assignedPlannerName,
        draftSince,
        durationDisplayUnit,
      } = payload.block;
      return state?.map((lane) => {
        if (lane.id === laneId) {
          return {
            ...lane,
            blocks: lane.blocks ?
              lane.blocks?.map((block) => {
                const additionalBlock = payload.additionalBlocks?.find(a => a.id === block.id);
                if (additionalBlock) {
                  return {
                    ...additionalBlock,
                    isDeleted: additionalBlock.showAs === 'hidden' || block.isDeleted,
                  };
                }
                if (id === block.id) {
                  if (block.shouldRenewData) {
                    return {
                      ...payload.block,
                      isDeleted: showAs === 'hidden' || block.isDeleted,
                    };
                  }
                  return {
                    ...block,
                    start,
                    line1,
                    badges,
                    duration,
                    subBlocks,
                    actions,
                    isDraft,
                    isAssignedPlanner,
                    isPreview: false,
                    showAs,
                    durationDisplayUnit,
                    lock,
                    assignedPlannerName,
                    draftSince,
                    isDeleted: showAs === 'hidden' || block.isDeleted,
                  };
                }
                return block;
              })
              : [],
          };
        }
        return lane;
      });
    },
    [actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_FAILURE]: (state, { payload }) =>
      state?.map((lane) => {
        if (lane.id === payload.laneId) {
          return {
            ...lane,
            blocks: lane.blocks?.map((block) => {
              if (block.isPreview) {
                return {
                  ...block,
                  isDeleted: true,
                };
              }
              return block;
            }),
          };
        }
        return lane;
      }),
    [actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_SUCCESS]: (state, { payload }) =>
      state?.map((lane) => {
        const blocks = lane.blocks || [];
        // if there are any blocks that we need to remove on this lane
        const toRemoveOnLane = payload.blocks?.filter(b => b.fromLaneId === lane.id);
        const toRemovePreview = blocks?.filter(b => b.isPreview);
        let filteredBlocks = [];

        if (toRemoveOnLane?.length || toRemovePreview?.length) {
          // delete blocks on lane
          filteredBlocks = blocks?.map((b) => {
            const foundBlock = toRemoveOnLane?.find(i => b.id.includes(i.id));
            const additionalBlock = payload.additionalBlocks?.find(a => a.id === b.id);
            if (additionalBlock) {
              return additionalBlock;
            }
            if (b.isPreview || foundBlock) {
              return {
                ...b,
                isDeleted: true,
              };
            }
            return b;
          });
          // update lane (from lane and to lane is the same)
          if (lane.id === payload.laneId) {
            return {
              ...lane,
              blocks: [...filteredBlocks, ...payload.blocks],
            };
          }
          // delete blocks from lane
          return {
            ...lane,
            blocks: [...filteredBlocks],
          };
        }
        // no changes on lane
        return lane;
      }),
    [actionTypes.FETCH_FORWARD_BLOCK_DATA_SUCCESS]: (state, { payload }) =>
      state?.map((lane) => {
        let newLane = lane;
        const matchingBlock = payload?.blocks?.find(payloadBlock =>
          lane.id === payloadBlock.laneId);
        if (matchingBlock) {
          const payloadBlock = lane.blocks.find(block =>
            block.id === matchingBlock.id,
          );
          if (payloadBlock) {
            // update block
            newLane = {
              ...lane,
              blocks: lane.blocks.map((block) => {
                if (block.id === matchingBlock.id) {
                  return {
                    ...block,
                    ...matchingBlock,
                  };
                }
                return block;
              }),
            };
          } else {
            // add block on lane if there not exist yet
            newLane = {
              ...lane,
              blocks: [
                ...lane.blocks.filter(e => !e.isPreview),
                matchingBlock,
              ],
            };
          }
        }
        return newLane;
      }),
    [actionTypes.FETCH_BLOCK_DATA_SUCCESS]: (state, { payload }) => {
      const { id, laneId } = payload;
      return state?.map((lane) => {
        if (lane.id === laneId) {
          return {
            ...lane,
            blocks: lane.blocks
              ? lane.blocks?.map((block) => {
                if (id === block.id && !block.isDeleted) {
                  return {
                    ...block,
                    ...payload,
                    isDeleted: payload.showAs === 'hidden' || block.isDeleted,
                  };
                }
                return block;
              })
              : [],
          };
        }
        return lane;
      });
    },
    [actionTypes.DELETE_LANE_DATA]: (state, { payload }) => {
      const { id, laneId } = payload;
      return state?.map((lane) => {
        if (lane.id === laneId) {
          return {
            ...lane,
            blocks: lane.blocks?.map((block) => {
              if (id === block.id) {
                return {
                  ...block,
                  isDeleted: true,
                };
              }
              return block;
            }),
          };
        }
        return lane;
      });
    },
    [actionTypes.UPDATE_BLOCK_ID]: (state, { payload }) => {
      const { from, to, laneId } = payload;
      return state?.map((lane) => {
        if (lane.id === laneId) {
          return {
            ...lane,
            blocks: lane.blocks?.map((block) => {
              if (from === block.id) {
                return {
                  ...block,
                  id: to,
                };
              }
              return block;
            }),
          };
        }
        return lane;
      });
    },
    [combineActions(
      UNLOCK_LOCK_SUCCESS,
      DISCARD_LOCK_SUCCESS,
    )]: (state, { payload }) => {
      const { id, laneId } = payload;
      return state?.map((lane) => {
        if (lane.id === laneId) {
          return {
            ...lane,
            blocks: lane.blocks?.map((block) => {
              if (id === block.id) {
                return {
                  ...block,
                  ...payload,
                };
              }
              return block;
            }),
          };
        }
        return lane;
      });
    },
    [actionTypes.UPDATE_BLOCK_OPERATION_INFO]: (state, { payload }) => {
      const { currentStatus, id, laneId } = payload;
      return state?.map((lane) => {
        if (lane.id === laneId) {
          return {
            ...lane,
            blocks: lane.blocks?.map((block) => {
              if (id === block.id) {
                return {
                  ...block,
                  operationInfo: currentStatus,
                };
              }
              return block;
            }),
          };
        }
        return lane;
      });
    },
    // Soft delete the block
    [actionTypes.DELETE_BLOCK_SUCCESS]: (state, { payload }) => {
      const { id, laneId } = payload.block;
      return state?.map((lane) => {
        if (lane.id === laneId) {
          return {
            ...lane,
            blocks: lane.blocks?.map((block) => {
              const additionalBlock = payload.additionalBlocks?.find(a => a.id === block.id);
              if (additionalBlock) {
                return additionalBlock;
              }
              if (id === block.id) {
                return {
                  ...payload.block,
                  isDeleted: block.isDeleted || block.isCopsOnly,
                };
              }
              return block;
            }),
          };
        }
        return lane;
      });
    },
    [actionTypes.DELETE_PREVIEW]: (state, { payload }) => state?.map((lane) => {
      const { laneIds, start, end } = payload;
      const foundLane = laneIds?.find(laneId => laneId === lane.id);
      if (foundLane) {
        return {
          ...lane,
          blocks: lane.blocks.filter(block =>
            block.type !== 'placeholder' && block.start !== start && block.end !== end),
        };
      }
      return lane;
    }),
    [actionTypes.CALCULATE_ROUTE_OPTIMISATION_SUCCESS]: (state, { payload }) =>
      state.map((lane) => {
        let newLane = lane;
        const foundLane = payload?.technicianIds?.find(laneId => laneId === lane.id);
        if (foundLane) {
          const preview = generatePreview({
            start: payload.from.startOf('day'),
            type: 'placeholder',
            laneId: foundLane.id,
            duration:
              moment.duration(
                payload.to.endOf('day').diff(payload.from.startOf('day')),
              ).format('DD.HH:mm:ss'),
            id: generatePreviewId(payload),
            entityType: ItemTypes.ORDER,
          });
          newLane = {
            ...lane,
            blocks: [...lane.blocks, { ...preview }],
          };
        }
        return newLane;
      }),
    [combineActions(
      actionTypes.ADD_BLOCK_DATA_SUCCESS,
      actionTypes.UPDATE_BLOCK_OTHER_LANE_DATA_SUCCESS,
    )]: (state, { payload }) => {
      const { id, laneId, objectId } = payload.block;
      return state?.map((lane) => {
        if (payload.oldLaneId && lane.id === payload.oldLaneId) {
          return {
            ...lane,
            blocks: lane.blocks?.map((b) => {
              const additionalBlock = payload.additionalBlocks?.find(a => a.id === b.id);
              if (additionalBlock) {
                return {
                  ...additionalBlock,
                  isDeleted: additionalBlock.showAs === 'hidden' || b?.isDeleted,
                };
              }

              if (id === b.id) {
                return {
                  ...b,
                  isDeleted: true,
                };
              }
              return b;
            }),
          };
        }
        if (lane.id === laneId) {
          const blocks = lane.blocks || [];
          const filteredBlocks = blocks?.map((b) => {
            const additionalBlock = payload.additionalBlocks?.find(a => a.id === b.id);
            if (additionalBlock) {
              return {
                ...additionalBlock,
                isDeleted: additionalBlock.showAs === 'hidden' || b?.isDeleted,
              };
            }
            if (
              b.isPreview &&
              !b.isDeleted &&
              (
                b.id === id ||
                b.id === generatePreviewId({ laneId, linkedEntityId: objectId })
              )
            ) {
              return {
                ...payload.block,
                isPreview: false,
              };
            }
            return b;
          });
          // Sockets don't have a preview so block should be added otherwise block was already
          // added as preview  with the request (see reducer) so this  would just update
          return {
            ...lane,
            blocks: payload.addBlockFromSocketUpdate ?
              [...filteredBlocks?.filter(fb => fb.id !== id), payload.block] :
              [...filteredBlocks],
          };
        }
        return lane;
      });
    },
    [combineActions(
      actionTypes.ADD_MULTI_BLOCK_DATA_SUCCESS,
    )]: (state, { payload }) => {
      const { laneId } = payload;
      return state?.map((lane) => {
        if (lane.id === laneId) {
          const blocks = lane.blocks || [];
          const filteredBlocks = blocks?.map((b) => {
            const additionalBlock = payload.additionalBlocks?.find(a => a.id === b.id);
            if (additionalBlock) {
              return additionalBlock;
            }
            if (b.isPreview && payload.blockIds?.find(i => b.id.includes(i))) {
              return {
                ...b,
                isDeleted: true,
              };
            }
            return b;
          });
          return {
            ...lane,
            blocks: [...filteredBlocks, ...payload.blocks],
          };
        }
        return lane;
      });
    },
    [combineActions(
      actionTypes.PUBLISH_TO_SAP_SUCCESS,
      actionTypes.PUBLISH_ALL_TO_SAP_SUCCESS,
    )]: (state, { payload }) => state?.map((lane) => {
      if (payload.succeeded?.find(b => b.laneId === lane.id)) {
        return {
          ...lane,
          blocks: lane.blocks ?
            lane.blocks?.map((block) => {
              const newBlock = payload.succeeded?.find(b => b.id === block.id);
              if (newBlock && !block.isDeleted) {
                return {
                  ...block,
                  ...newBlock,
                };
              }
              return block;
            })
            : [],
        };
      }
      return lane;
    }),
    [combineActions(
      actionTypes.RESTORE_BACKUP_SUCCESS,
    )]: (state, { payload }) => state?.map((lane) => {
      const blocks = lane.blocks ?
        lane.blocks?.filter(b =>
          b.id !== payload.block.id && !payload.removedBlocks.includes(b.id),
        ) :
        [];

      return {
        ...lane,
        blocks: lane.id === payload.block.laneId ?
          [
            ...blocks,
            payload.block,
          ] :
          blocks,
      };
    }),
    [combineActions(
      actionTypes.MARK_DATE_CONFIRMED_SUCCESS,
    )]: (state, { payload }) => state?.map((lane) => {
      if (lane.id === payload.laneId) {
        return {
          ...lane,
          blocks: lane.blocks ?
            lane.blocks?.map((block) => {
              if (payload.id === block.id && !block.isDeleted) {
                return {
                  ...block,
                  ...payload,
                };
              }
              return block;
            })
            : [],
        };
      }
      return lane;
    }),
    [combineActions(
      actionTypes.MARK_DATE_UNCONFIRMED_SUCCESS,
    )]: (state, { payload }) => state?.map((lane) => {
      if (lane.id === payload.laneId) {
        return {
          ...lane,
          blocks: lane.blocks ?
            lane.blocks?.map((block) => {
              if (payload.id === block.id && !block.isDeleted) {
                return {
                  ...block,
                  ...payload,
                };
              }
              return block;
            })
            : [],
        };
      }
      return lane;
    }),
    [combineActions(
      actionTypes.SPLIT_BLOCK_SUCCESS,
    )]: (state, { payload }) => state?.map((lane) => {
      if (lane.id === payload.originalBlock.laneId) {
        return {
          ...lane,
          blocks: [
            ...lane.blocks?.map((block) => {
              if (payload.originalBlock.id === block.id && !block.isDeleted) {
                return payload.originalBlock;
              }
              return block;
            }),
            payload.splittedBlock,
          ],
        };
      }
      return lane;
    }),
    [combineActions(
      PUBLISH_TO_SAP_QUICK_ACTION_SUCCESS,
    )]: (state, { payload }) => state?.map((lane) => {
      const filteredBlocksByLane = payload.succeeded?.filter(b => b.laneId === lane.id);
      if (filteredBlocksByLane?.length) {
        return {
          ...lane,
          blocks: [
            ...lane.blocks?.map((block) => {
              const foundBlock = filteredBlocksByLane.find(b => b.id === block.id);
              if (foundBlock && !block.isDeleted) {
                return foundBlock;
              }
              return block;
            }),
          ],
        };
      }
      return lane;
    }),
    [combineActions(
      ALERT_SNOOZE_SUCCESS,
      ALERT_DISCARD_SUCCESS,
    )]: (state, { payload }) => {
      if (payload.blockId && payload.laneId) {
        return state?.map((lane) => {
          if (lane.id === payload.laneId) {
            return {
              ...lane,
              blocks: lane.blocks ?
                lane.blocks?.map((block) => {
                  if (payload.blockId === block.id) {
                    const alerts = block.alert.alerts?.filter(a => a.id !== payload.id);
                    const newAlerts = alerts.length ?
                      alerts?.sort((a, b) => a.severity - b.severity) :
                      [];
                    const badgeText = newAlerts.length ?
                      blockConfig.alertsBySeverity[newAlerts[0].severity].badgeText :
                      blockConfig.alertsBySeverity[0].badgeText;
                    const level = newAlerts.length ?
                      blockConfig.alertsBySeverity[newAlerts[0].severity].level :
                      blockConfig.alertsBySeverity[0].level;
                    return {
                      ...block,
                      alert: {
                        ...block.alert,
                        alerts,
                        id: newAlerts[0]?.id || null,
                        badgeText,
                        level,
                      },
                    };
                  }
                  return block;
                })
                : [],
            };
          }
          return lane;
        });
      }
      return state;
    },
    // Drop preview handling
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA,
      actionTypes.SET_BLOCK_PREVIEW,
    )]: (state, { payload }) => {
      const {
        planningId,
        laneId,
        start,
        oldLaneId,
        skipPreview,
      } = payload;
      return state?.map((lane) => {
        if (oldLaneId && lane.id === oldLaneId) {
          return {
            ...lane,
            blocks: lane.blocks ?
              lane.blocks?.map((block) => {
                if (planningId === block.id) {
                  // Add fallback start in case the update does not work
                  return {
                    ...block,
                    isDeleted: true,
                  };
                }
                return block;
              })
              : [],
          };
        }
        if (lane.id === laneId) {
          if (oldLaneId || !lane.blocks?.find(b => b.id === planningId)) {
            if (skipPreview) {
              return lane;
            }
            const blocks = lane.blocks || [];
            const preview = generatePreview({
              start,
              type: blockConfig.types.fieldService,
              laneId,
              id: planningId,
              entityType: ItemTypes.ORDER,
            });
            return {
              ...lane,
              blocks: [
                ...blocks,
                {
                  ...preview,
                  shouldRenewData: !lane.blocks?.find(b => b.id === planningId),
                },
              ],
            };
          }
          return {
            ...lane,
            blocks: lane.blocks ?
              lane.blocks?.map((block) => {
                if (planningId === block.id) {
                  // Add fallback start in case the update does not work
                  return {
                    ...block,
                    fallbackStart: block.start,
                    isPreview: true,
                    start,
                  };
                }
                return block;
              })
              : [],
          };
        }
        return lane;
      });
    },
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA_FAILURE,
      actionTypes.SET_BLOCK_PREVIEW_FAILURE,
    )]: (state, { payload }) => {
      const {
        planningId,
        laneId,
        oldLaneId,
      } = payload;

      return state?.map((lane) => {
        if (oldLaneId && lane.id === oldLaneId) {
          return {
            ...lane,
            blocks: lane.blocks ?
              lane.blocks?.map((block) => {
                if (planningId === block.id) {
                  // Add fallback start in case the update does not work
                  return {
                    ...block,
                    isDeleted: false,
                  };
                }
                return block;
              })
              : [],
          };
        }
        if (lane.id === laneId) {
          if (oldLaneId) {
            return {
              ...lane,
              blocks: lane.blocks ?
                lane.blocks?.map((block) => {
                  if (
                    block.isPreview &&
                    (
                      block.id === planningId ||
                      block.id === generatePreviewId(payload)
                    )
                  ) {
                    return {
                      ...block,
                      isDeleted: true,
                    };
                  }
                  return block;
                })
                : [],
            };
          }
          return {
            ...lane,
            blocks: lane.blocks ?
              lane.blocks?.map((block) => {
                if (planningId === block.id) {
                  // Add fallback start in case the update does not work
                  return {
                    ...block,
                    isPreview: false,
                    start: block.fallbackStart,
                  };
                }
                return block;
              })
              : [],
          };
        }
        return lane;
      });
    },
    [actionTypes.ADD_BLOCK_DATA]: (state, { payload }) => {
      const { laneId, start, linkedEntityType, type, duration } = payload;
      return state?.map((lane) => {
        if (lane.id === laneId) {
          const blocks = lane.blocks || [];
          const preview = generatePreview({
            start,
            type,
            laneId,
            // generate id because new blocks don't have an id yet.
            id: generatePreviewId(payload),
            linkedEntityId: payload.linkedEntityId,
            entityType: linkedEntityType,
            duration,
          });
          return {
            ...lane,
            blocks: [...blocks, { ...preview }],
          };
        }
        return lane;
      });
    },
    [combineActions(
      actionTypes.ADD_BLOCK_DATA_FAILURE,
      actionTypes.ADD_MULTI_BLOCK_DATA_FAILURE,
    )]: (state, { payload }) => {
      const { laneId, id } = payload;
      return state?.map((lane) => {
        if (lane.id === laneId) {
          const blocks = lane.blocks || [];
          return {
            ...lane,
            blocks: blocks?.map((b) => {
              if (b.isPreview && b.id === id) {
                return {
                  ...b,
                  isDeleted: true,
                };
              }
              return b;
            }),
          };
        }
        return lane;
      });
    },
    [combineActions(
      actionTypes.ADD_MULTI_BLOCK_DATA,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA,
    )]: (state, { payload }) => {
      // check if we have a planning item or and order item (default on order)
      const linkedEntityType = payload.entities.every(e =>
        e.linkedEntityType === payload.entities[0].linkedEntityType)
        ? payload.entities[0].linkedEntityType
        : 'order';

      const { laneId, start } = payload;
      const preview = generatePreview({
        start,
        type: blockConfig.types.fieldService,
        laneId,
        id: generatePreviewId({
          laneId,
          linkedEntityId: payload.entities?.map(e => e.linkedEntityId)
            .join(urlSigns.keyValueSeparator),
        }),
        entityType: linkedEntityType,
      });

      return state?.map((lane) => {
        if (lane.id === laneId) {
          const blocks = lane.blocks || [];
          return {
            ...lane,
            blocks: [...blocks, { ...preview }],
          };
        }
        return lane;
      });
    },
    [CREATE_AUTO_ORDERS]: (state, { payload }) => {
      const { workcenterId, newDate, shouldUpdatePlanningboard } = payload;
      if (workcenterId && newDate && shouldUpdatePlanningboard) {
        const preview = generatePreview({
          start: newDate,
          type: blockConfig.types.fieldService,
          laneId: workcenterId,
          id: generatePreviewId({
            laneId: workcenterId,
            linkedEntityId: payload.actions?.map(e => e.id).join(urlSigns.keyValueSeparator),
          }),
          entityType: 'order',
        });

        return state?.map((lane) => {
          if (lane.id === workcenterId) {
            const blocks = lane.blocks || [];
            return {
              ...lane,
              blocks: [...blocks, { ...preview }],
            };
          }
          return lane;
        });
      }
      return state;
    },
    [CREATE_AUTO_ORDERS_SUCCESS]: (state, { payload }) => {
      const { workcenterIds = [], id, shouldUpdatePlanningboard } = payload;
      if (workcenterIds?.length && shouldUpdatePlanningboard) {
        return state?.map((lane) => {
          if (workcenterIds?.includes(lane?.id)) {
            const blocks = (lane.blocks || [])?.map((block) => {
              // delete blocks that are about to double in the lane
              const found = payload?.planningBlocks?.find(pb => pb?.id === block?.id);
              if (found) {
                return {
                  ...block,
                  isDeleted: true,
                };
              }
              return block;
            });
            const filteredBlocks = blocks?.map((b) => {
              if (b.isPreview && b.id === id) {
                return {
                  ...b,
                  isDeleted: true,
                };
              }
              return b;
            });
            return {
              ...lane,
              blocks: [
                ...filteredBlocks,
                ...payload.planningBlocks?.filter(pb => pb.laneId === lane.id),
              ],
            };
          }
          return lane;
        });
      }
      return state;
    },
    [CREATE_AUTO_ORDERS_FAILURE]: (state, { payload }) => {
      const { workcenterId, id, shouldUpdatePlanningboard } = payload;
      if (workcenterId && shouldUpdatePlanningboard) {
        return state?.map((lane) => {
          if (lane.id === workcenterId) {
            const blocks = lane.blocks || [];
            return {
              ...lane,
              blocks: blocks?.map((b) => {
                if (b.isPreview && b.id === id) {
                  return {
                    ...b,
                    isDeleted: true,
                  };
                }
                return b;
              }),
            };
          }
          return lane;
        });
      }
      return state;
    },
    [AUTO_PLANNING_ACTION_SUCCESS]: (state, { payload }) => {
      const { successfulBlocks } = payload;

      if (payload.action === 'deleteUnpublishedPlanningBackground') {
        return state?.map(lane => ({
          ...lane,
          blocks: lane?.blocks?.map((b) => {
            const foundBlock = successfulBlocks?.find(i => b.id.includes(i.id));
            if (foundBlock) {
              return {
                ...b,
                isDeleted: true,
              };
            }
            return b;
          }) || [],
        }));
      }

      if (payload.action !== 'revertPlanningBackground') {
        if (payload.shouldUpdatePlanningboard) {
          return state?.map((lane) => {
            const blocks = (lane.blocks || []).map((b) => {
              const block = successfulBlocks.find(s => s.id === b.id);
              if (block) {
                return {
                  ...block,
                  isPreview: false,
                  isDeleted: b.isDeleted || block.fromLaneId === lane.id,
                };
              }
              return b;
            });
            const newBlocks = successfulBlocks.filter(b =>
              b.fromLaneId &&
              b.laneId === lane.id,
            ) || [];

            return {
              ...lane,
              blocks: [
                ...blocks,
                ...newBlocks,
              ],
            };
          });
        }
        return state;
      }

      return state?.map((lane) => {
        const blocksInLane = successfulBlocks?.filter(b => b.laneId === lane.id);
        const blocks = lane.blocks ?
          lane.blocks?.filter(b =>
            !successfulBlocks?.find(bl => bl.id === b.id) && !payload.removedBlocks?.includes(b.id),
          ) :
          [];

        return {
          ...lane,
          blocks: blocksInLane?.length ?
            [
              ...blocks,
              ...blocksInLane,
            ] :
            blocks,
        };
      });
    },
  },
  [],
);

export const ganttDimensions = handleActions(
  {
    [actionTypes.SET_GANTT_DIMENSIONS]: (state, { payload }) => payload,
  },
  {
    widths: {},
    view: {},
  },
);

export const viewDimensions = handleActions(
  {
    [actionTypes.SET_VIEW_DIMENSIONS]: (state, { payload }) => payload,
  },
  {
    widths: {},
    view: {},
  },
);

export const dateReference = handleActions(
  {
    [actionTypes.RESET_BOARD_DATA]: () => ({
      day: moment().date(),
      week: moment().week(),
      month: moment().month(),
      year: moment().year(),
    }),
    [actionTypes.SET_DATE_REFERENCE]: (state, { payload }) => payload,
  },
  {
    day: moment().date(),
    week: moment().week(),
    month: moment().month(),
    year: moment().year(),
  },
);

export const view = handleActions(
  {
    [actionTypes.SET_VIEW_INTERNAL]: (state, { payload }) => {
      // force to take the first working day as start of the week for workweeks
      const { parameters: { startOfWeek, workingDays } } = payload;
      payload.parameters.startOfWeek =
        workingDays.includes(startOfWeek) ? startOfWeek : workingDays[0];
      return payload;
    },
    [actionTypes.RESET_BOARD_DATA]: () => ({}),
    [actionTypes.SET_VIEW_WORKING_DAYS]: (state, { payload }) => ({
      ...state,
      parameters: {
        ...state.parameters,
        workingDays: payload,
        startOfWeek: payload && payload[0],
      },
    }),
  },
  {},
);

export const planningAndViewToOpen = handleActions(
  {
    [actionTypes.OPEN_PLANNING_AND_VIEW_SUCCESS]: (state, { payload }) => payload,
    [combineActions(
      actionTypes.OPEN_PLANNING_AND_VIEW,
      actionTypes.OPEN_PLANNING_AND_VIEW_FAILURE,
      actionTypes.RESET_OPEN_PLANNING_AND_VIEW,
    )]: () => ({}),
  },
  {},
);

export const boardData = handleActions(
  {
    [actionTypes.FETCH_BOARD_DATA_SUCCESS]: (state, { payload }) => ({
      ...payload,
      views: payload?.views?.map((v) => {
        if (v?.id === viewIds.oneDay) {
          v.parameters.workingDays = [moment().day()];
        }
        if (v?.id === viewIds.twoDays) {
          const day = moment().day();
          v.parameters.workingDays = [day, day + 1];
        }
        if (v?.type === 'workhours') {
          // eslint-disable-next-line prefer-destructuring
          v.parameters.startOfWeek = v.parameters.workingDays[0];
        }
        return v;
      }),
    }),
    [combineActions(
      actionTypes.RESET_BOARD_DATA,
      actionTypes.FETCH_BOARD_DATA_FAILURE,
    )]: () => ({}),
    [combineActions(
      actionTypes.FETCH_BOARD_DATA,
    )]: (state, { payload }) => payload?.keepRows ? state : {},
  },
  {},
);

export const totalDataCount = handleActions(
  {
    [actionTypes.FETCH_BOARD_DATA_SUCCESS]: (state, { payload }) => payload.totalDataCount,
    [combineActions(
      SET_ACTIVE_SERVICE_CENTER,
      actionTypes.FETCH_BOARD_DATA_FAILURE,
      actionTypes.RESET_BOARD_DATA,
    )]: () => 0,
  },
  0,
);

export const addedBlockIds = handleActions(
  {
    [actionTypes.ADD_BLOCK_DATA_SUCCESS]: (state, { payload }) => [payload.block.objectId],
    [combineActions(
      actionTypes.ADD_MULTI_BLOCK_DATA_SUCCESS,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_SUCCESS,
    )]: (state, { payload }) => payload.blockIds,
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.ADD_BLOCK_DATA,
      actionTypes.ADD_MULTI_BLOCK_DATA,
    )]: () => null,
  },
  null,
);

export const amountPlanningsToPublish = handleActions(
  {
    [actionTypes.FETCH_PUBLISH_ALL_COUNT_SUCCESS]: (state, { payload }) =>
      payload.amountPlanningsToPublish,
    [combineActions(
      actionTypes.FETCH_PUBLISH_ALL_COUNT_FAILURE,
    )]: () => 0,
  },
  0,
);

export const amountPlanningsWithPublishingError = handleActions(
  {
    [actionTypes.FETCH_PUBLISH_ALL_COUNT_SUCCESS]: (state, { payload }) =>
      payload.amountFailedPlanningsToPublish,
    [actionTypes.FETCH_PUBLISH_ALL_COUNT_FAILURE]: () => 0,
  },
  0,
);

export const isLoadingPublishButton = handleActions(
  {
    [combineActions(
      actionTypes.FETCH_PUBLISH_ALL_COUNT_SUCCESS,
      actionTypes.FETCH_PUBLISH_ALL_COUNT_FAILURE,
    )]: () =>
        false,
    [combineActions(
      actionTypes.FETCH_PUBLISH_ALL_COUNT,
    )]: () => true,
  },
  false,
);


export const planningsToPublish = handleActions(
  {
    [actionTypes.FETCH_PUBLISH_ALL_COUNT_SUCCESS]: (state, { payload }) =>
      payload.planningsToPublish,
    [combineActions(
      actionTypes.FETCH_PUBLISH_ALL_COUNT_FAILURE,
    )]: () => [],
  },
  [],
);

export const planningToPublishMessage = handleActions(
  {
    [actionTypes.PUBLISH_TO_SAP_SUCCESS]: (state, { payload }) => {
      let type = 'warn';
      if (payload.succeededCount === payload.totalCount) {
        type = 'success';
      } else if (payload.failedCount === payload.totalCount) {
        type = 'error';
      }
      return {
        message: payload.message,
        type,
      };
    },
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.PUBLISH_TO_SAP,
    )]: () => ({}),
  },
  {},
);

export const currentPage = handleActions(
  {
    [actionTypes.SET_CURRENT_PAGE]: (state, { payload }) => payload,
    [actionTypes.RESET_BOARD_DATA]: () => 1,
  },
  1,
);

export const viewData = handleActions(
  {
    [actionTypes.FETCH_VIEW_DATA_SUCCESS]: (state, { payload }) => {
      if (payload.refetchSubSet) {
        return {
          ...state,
          lanes: state.lanes.map((lane) => {
            let newLane = lane;
            const matchingLanes = payload?.lanes?.find(payloadLane =>
              lane.id === payloadLane.id);
            if (matchingLanes) {
              newLane = matchingLanes;
            }

            return newLane;
          }),
        };
      }
      return payload;
    },
    [actionTypes.FETCH_VIEW_DATA]: (state, { payload }) => payload.refetchSubSet ? state : {},
    [combineActions(
      actionTypes.RESET_BOARD_DATA,
      actionTypes.FETCH_VIEW_DATA_FAILURE,
    )]: () => ({}),
  },
  {},
);

export const isLoadingViewData = handleActions(
  {
    [actionTypes.OPEN_PLANNING_AND_VIEW]: () => true,
    [actionTypes.FETCH_VIEW_DATA]: (state, { payload }) =>
      !payload.refetchSubSet,
    [combineActions(
      actionTypes.OPEN_PLANNING_AND_VIEW_SUCCESS,
      actionTypes.OPEN_PLANNING_AND_VIEW_FAILURE,
      actionTypes.RESET_OPEN_PLANNING_AND_VIEW,
      actionTypes.FETCH_VIEW_DATA_SUCCESS,
      actionTypes.FETCH_VIEW_DATA_FAILURE,
    )]: () => false,
  },
  false,
);

export const isLoadingBoardData = handleActions(
  {
    [actionTypes.FETCH_BOARD_DATA]: (state, { payload }) => !payload?.keepRows,
    [combineActions(
      actionTypes.FETCH_BOARD_DATA_SUCCESS,
      actionTypes.FETCH_BOARD_DATA_FAILURE,
    )]: () => false,
  },
  false,
);

export const isLoadingBlockData = handleActions(
  {
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA,
      actionTypes.ADD_BLOCK_DATA,
      actionTypes.FETCH_BLOCK_DATA,
      actionTypes.ADD_MULTI_BLOCK_DATA,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA
    )]: () => true,
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA_FAILURE,
      actionTypes.UPDATE_BLOCK_DATA_SUCCESS,
      actionTypes.UPDATE_BLOCK_OTHER_LANE_DATA_SUCCESS,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_SUCCESS,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_FAILURE,
      actionTypes.ADD_BLOCK_DATA_FAILURE,
      actionTypes.ADD_BLOCK_DATA_SUCCESS,
      actionTypes.ADD_MULTI_BLOCK_DATA_FAILURE,
      actionTypes.ADD_MULTI_BLOCK_DATA_SUCCESS,
      actionTypes.FETCH_BLOCK_DATA_FAILURE,
      actionTypes.FETCH_BLOCK_DATA_SUCCESS,
    )]: () => false,
  },
  false,
);

export const isLoadingSidepanelBlockData = handleActions(
  {
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA,
      actionTypes.ADD_BLOCK_DATA,
      actionTypes.FETCH_BLOCK_DATA,
    )]: (state, { payload }) => {
      if (payload.isSidePanel) {
        if (payload.skipReload) {
          return false;
        }
        return true;
      }
      return state;
    },
    [combineActions(
      actionTypes.UPDATE_DATA_FROM_SOCKETS,
    )]: () => false,
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA_FAILURE,
      actionTypes.UPDATE_BLOCK_DATA_SUCCESS,
      actionTypes.UPDATE_BLOCK_OTHER_LANE_DATA_SUCCESS,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_SUCCESS,
      actionTypes.ADD_BLOCK_DATA_FAILURE,
      actionTypes.ADD_BLOCK_DATA_SUCCESS,
      actionTypes.FETCH_BLOCK_DATA_FAILURE,
      actionTypes.FETCH_BLOCK_DATA_SUCCESS
    )]: (state, { payload }) => {
      if (payload.isSidePanel) {
        return false;
      }
      return state;
    },
  },
  false,
);


export const block = handleActions(
  {
    [actionTypes.RESET_BLOCK_DATA]: () => ({}),
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.FETCH_BLOCK_DATA,
      actionTypes.FETCH_BLOCK_DATA_FAILURE,
    )]: (state, { payload }) => {
      if (payload.isSidePanel) {
        return {};
      }
      return state;
    },
    [combineActions(
      actionTypes.MARK_DATE_CONFIRMED_SUCCESS,
    )]: (state, { payload }) => {
      if (payload.isSidePanel) {
        return payload;
      }
      return state;
    },
    [combineActions(
      actionTypes.RESTORE_BACKUP_SUCCESS,
      actionTypes.RECALCULATE_TRAVEL_TIME_SUCCESS,
    )]: (state, { payload }) => {
      if (payload.isSidePanel) {
        return payload.block;
      }
      return state;
    },
    [combineActions(
      actionTypes.FETCH_BLOCK_DATA_SUCCESS,
      actionTypes.MARK_DATE_UNCONFIRMED_SUCCESS,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_SUCCESS,
      actionTypes.DELETE_BLOCK_SUCCESS
    )]: (state, { payload }) => {
      if (payload.isSidePanel) {
        if (payload.showAs === 'hidden') {
          return {
            ...payload,
            isDeleted: true,
          };
        }
        return payload;
      }
      return state;
    },
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA_SUCCESS,
      actionTypes.UPDATE_BLOCK_OTHER_LANE_DATA_SUCCESS,
    )]: (state, { payload }) => {
      if (payload.isSidePanel) {
        if (payload.block.showAs === 'hidden') {
          return {
            ...payload.block,
            isDeleted: true,
          };
        }
        return payload.block;
      }
      return state;
    },
    [combineActions(
      actionTypes.FETCH_PLANNING_DATA_POST_SOCKET_UPDATE_SUCCESS,
      PLANNING_UPDATE_ORDER_SOCKET_EVENTS_SUCCESS,
    )]: (state, { payload }) => {
      if (payload?.id === state?.id) {
        if (payload.showAs === 'hidden') {
          return {
            ...payload,
            isDeleted: true,
          };
        }
        return payload;
      }
      return state;
    },
    [combineActions(
      actionTypes.PUBLISH_TO_SAP_SUCCESS,
    )]: (state, { payload }) => {
      if (payload.isSidePanel) {
        return payload.succeeded[0];
      }
      return state;
    },
    [combineActions(
      ALERT_SNOOZE_SUCCESS,
      ALERT_DISCARD_SUCCESS,
    )]: (state, { payload }) => {
      if (payload.blockId && payload.laneId) {
        const alerts = state.alert.alerts?.filter(a => a.id !== payload.id);
        const newAlerts = alerts.length ?
          alerts?.sort((a, b) => a.severity - b.severity) :
          [];
        const badgeText = newAlerts.length ?
          blockConfig.alertsBySeverity[newAlerts[0].severity].badgeText :
          blockConfig.alertsBySeverity[0].badgeText;
        const level = newAlerts.length ?
          blockConfig.alertsBySeverity[newAlerts[0].severity].level :
          blockConfig.alertsBySeverity[0].level;
        return {
          ...state,
          alert: {
            ...block.alert,
            alerts,
            id: newAlerts[0]?.id || null,
            badgeText,
            level,
          },
        };
      }
      return state;
    },
    [actionTypes.UPDATE_BLOCK_ID]: (state, { payload }) => {
      const { from, to } = payload;
      if (state.id === from) {
        return {
          ...state,
          id: to,
        };
      }
      return state;
    },
    [combineActions(
      UNLOCK_LOCK_SUCCESS,
      DISCARD_LOCK_SUCCESS,
    )]: (state, { payload }) => payload,
  },
  {},
);

export const partiallyExecuted = handleActions(
  {
    LOCATION_CHANGE: () => ({}),
    [actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_SUCCESS]: (state, { payload }) => {
      const { failedCount, failedIds, totalCount } = payload;
      return failedCount > 0 ? {
        failedCount,
        failedIds,
        totalCount,
      } : {};
    },
  },
  {},
);

export const error = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.FETCH_BOARD_DATA_SUCCESS,
      actionTypes.FETCH_VIEW_DATA_SUCCESS,
      actionTypes.FETCH_BOARD_DATA,
      actionTypes.FETCH_VIEW_DATA,
      actionTypes.UPDATE_BLOCK_DATA_SUCCESS,
      actionTypes.UPDATE_BLOCK_DATA,
      actionTypes.FETCH_BLOCK_DATA_SUCCESS,
      actionTypes.FETCH_BLOCK_DATA,
      actionTypes.MARK_DATE_CONFIRMED,
      actionTypes.MARK_DATE_CONFIRMED_SUCCESS,
      actionTypes.MARK_DATE_UNCONFIRMED,
      actionTypes.MARK_DATE_UNCONFIRMED_SUCCESS,
      actionTypes.RESTORE_BACKUP,
      actionTypes.RESTORE_BACKUP_SUCCESS,
      actionTypes.PUBLISH_TO_SAP,
      actionTypes.PUBLISH_TO_SAP_SUCCESS,
      actionTypes.PUBLISH_ALL_TO_SAP,
      actionTypes.PUBLISH_ALL_TO_SAP_SUCCESS,
      actionTypes.DELETE_BLOCK,
      actionTypes.DELETE_BLOCK_SUCCESS,
      actionTypes.ADD_BLOCK_DATA,
      actionTypes.ADD_BLOCK_DATA_SUCCESS,
      actionTypes.ADD_MULTI_BLOCK_DATA,
      actionTypes.ADD_MULTI_BLOCK_DATA_SUCCESS,
      AUTO_PLANNING_ACTION,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_SUCCESS,
      actionTypes.CREATE_GANTT_VIEW_REQUEST,
      actionTypes.CREATE_GANTT_VIEW_SUCCESS,
      actionTypes.UPDATE_GANTT_VIEWS_REQUEST,
      actionTypes.UPDATE_GANTT_VIEWS_SUCCESS,
      actionTypes.FETCH_BOARD_METADATA,
      actionTypes.FETCH_BOARD_METADATA_SUCCESS,
      actionTypes.RECALCULATE_TRAVEL_TIME,
      actionTypes.RECALCULATE_TRAVEL_TIME_SUCCESS,
      actionTypes.CALCULATE_ROUTE_OPTIMISATION_SUCCESS,
      actionTypes.ABORT_ROUTE_OPTIMISATION,
      actionTypes.ABORT_ROUTE_OPTIMISATION_SUCCESS,
      actionTypes.FETCH_PLANNING_DATA_POST_SOCKET_UPDATE_SUCCESS,
      actionTypes.FETCH_PLANNING_DATA_POST_SOCKET_UPDATE,
    )]: () => ({}),
    [combineActions(
      actionTypes.FETCH_VIEW_DATA_FAILURE,
      actionTypes.FETCH_BOARD_DATA_FAILURE,
      actionTypes.UPDATE_BLOCK_DATA_FAILURE,
      actionTypes.FETCH_BLOCK_DATA_FAILURE,
      actionTypes.MARK_DATE_CONFIRMED_FAILURE,
      actionTypes.MARK_DATE_UNCONFIRMED_FAILURE,
      actionTypes.RESTORE_BACKUP_FAILURE,
      actionTypes.PUBLISH_TO_SAP_FAILURE,
      actionTypes.PUBLISH_ALL_TO_SAP_FAILURE,
      actionTypes.ADD_BLOCK_DATA_FAILURE,
      actionTypes.ADD_MULTI_BLOCK_DATA_FAILURE,
      AUTO_PLANNING_ACTION_FAILURE,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_FAILURE,
      actionTypes.CREATE_GANTT_VIEW_FAILURE,
      actionTypes.UPDATE_GANTT_VIEWS_FAILURE,
      actionTypes.DELETE_BLOCK_FAILURE,
      actionTypes.FETCH_BOARD_METADATA_FAILURE,
      actionTypes.RECALCULATE_TRAVEL_TIME_FAILURE,
      actionTypes.CALCULATE_ROUTE_OPTIMISATION_FAILURE,
      actionTypes.ABORT_ROUTE_OPTIMISATION_FAILURE,
      actionTypes.FETCH_PLANNING_DATA_POST_SOCKET_UPDATE_FAILURE,
    )]: (
      state,
      { payload },
    ) => payload?.error,
  },
  {},
);

export const isDoingAction = handleActions(
  {
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA,
      actionTypes.MARK_DATE_CONFIRMED,
      actionTypes.MARK_DATE_UNCONFIRMED,
      actionTypes.RESTORE_BACKUP,
      actionTypes.PUBLISH_TO_SAP,
      actionTypes.DELETE_BLOCK,
      actionTypes.PUBLISH_ALL_TO_SAP,
      actionTypes.RECALCULATE_TRAVEL_TIME,
    )]: () => true,
    [combineActions(
      actionTypes.UPDATE_BLOCK_OTHER_LANE_DATA_SUCCESS,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_SUCCESS,
      actionTypes.UPDATE_BLOCK_DATA_SUCCESS,
      actionTypes.MARK_DATE_CONFIRMED_SUCCESS,
      actionTypes.PUBLISH_TO_SAP_SUCCESS,
      actionTypes.UPDATE_BLOCK_DATA_FAILURE,
      actionTypes.MARK_DATE_CONFIRMED_FAILURE,
      actionTypes.PUBLISH_TO_SAP_FAILURE,
      actionTypes.DELETE_BLOCK_SUCCESS,
      actionTypes.DELETE_BLOCK_FAILURE,
      actionTypes.PUBLISH_ALL_TO_SAP_SUCCESS,
      actionTypes.PUBLISH_ALL_TO_SAP_FAILURE,
      actionTypes.RESTORE_BACKUP_SUCCESS,
      actionTypes.RESTORE_BACKUP_FAILURE,
      actionTypes.MARK_DATE_UNCONFIRMED_FAILURE,
      actionTypes.MARK_DATE_UNCONFIRMED_SUCCESS,
      actionTypes.RECALCULATE_TRAVEL_TIME_FAILURE,
      actionTypes.RECALCULATE_TRAVEL_TIME_SUCCESS,
      actionTypes.UPDATE_DATA_FROM_SOCKETS,
    )]: () => false,
  },
  false,
);

export const saveSuccess = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.UPDATE_BLOCK_DATA,
    )]: () => false,
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA_SUCCESS,
      actionTypes.UPDATE_BLOCK_DATA_FAILURE,
      actionTypes.UPDATE_BLOCK_OTHER_LANE_DATA_SUCCESS,
      actionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_SUCCESS,
    )]: () => true,
  },
  false,
);

export const addSuccess = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.ADD_BLOCK_DATA,
    )]: () => false,
    [combineActions(
      actionTypes.ADD_BLOCK_DATA_SUCCESS,
      actionTypes.ADD_BLOCK_DATA_FAILURE,
    )]: (state, { payload }) => !!payload.isSidePanel,
  },
  false,
);

export const activeFilters = handleActions(
  {
    [actionTypes.RESET_BOARD_DATA]: () => ({}),
    [actionTypes.UPDATE_GANTT_FILTERS]: (state, { payload }) => payload,
  },
  {},
);

export const activeSort = handleActions(
  {
    [actionTypes.RESET_BOARD_DATA]: () => '',
    [actionTypes.UPDATE_GANTT_SORT]: (state, { payload }) => payload,
  },
  '',
);

export const activeView = handleActions(
  {
    [actionTypes.RESET_BOARD_DATA]: () => null,
    [actionTypes.UPDATE_GANTT_VIEW]: (state, { payload }) => payload,
  },
  null,
);

export const filterDefinitions = handleActions(
  {
    [actionTypes.FETCH_BOARD_DATA_SUCCESS]: (state, { payload }) => payload.filters,
    [combineActions(actionTypes.FETCH_BOARD_DATA, actionTypes.FETCH_BOARD_DATA_FAILURE)]: state =>
      state,
  },
  [],
);

export const isDraggingOver = handleActions(
  {
    [actionTypes.SET_IS_DRAGGING_OVER]: (state, { payload }) => payload,
  },
  false,
);

export const publishAllSuccessMessage = handleActions(
  {
    [actionTypes.PUBLISH_ALL_TO_SAP_SUCCESS]: (state, { payload }) => {
      let type = 'warn';
      if (payload.succeededCount === payload.totalCount) {
        type = 'success';
      } else if (payload.failedCount === payload.totalCount) {
        type = 'error';
      }
      return {
        message: payload.message,
        type,
      };
    },
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.PUBLISH_ALL_TO_SAP,
      actionTypes.PUBLISH_ALL_TO_SAP_FAILURE,
    )]: () => ({}),
  },
  {},
);

export const views = handleActions(
  {
    [combineActions(
      actionTypes.RESET_BOARD_DATA,
      actionTypes.GET_GANTT_VIEWS_FAILURE,
    )]: () => null,
    [combineActions(
      actionTypes.GET_GANTT_VIEWS_SUCCESS,
      actionTypes.UPDATE_GANTT_VIEWS_SUCCESS,
      actionTypes.CREATE_GANTT_VIEW_SUCCESS,
    )]: (state, { payload }) => [...payload, defaultGanttView],
  },
  null,
);

export const isLoadingViews = handleActions(
  {
    [combineActions(
      actionTypes.GET_GANTT_VIEWS_SUCCESS,
      actionTypes.GET_GANTT_VIEWS_FAILURE,
    )]: () => false,
    [actionTypes.GET_GANTT_VIEWS_REQUEST]: () => true,
  },
  false,
);

export const isSavingViews = handleActions(
  {
    [combineActions(
      actionTypes.CREATE_GANTT_VIEW_SUCCESS,
      actionTypes.CREATE_GANTT_VIEW_FAILURE,
      actionTypes.UPDATE_GANTT_VIEWS_SUCCESS,
      actionTypes.UPDATE_GANTT_VIEWS_FAILURE,
    )]: () => false,
    [combineActions(
      actionTypes.CREATE_GANTT_VIEW_REQUEST,
      actionTypes.UPDATE_GANTT_VIEWS_REQUEST,
    )]: () => true,
  },
  false,
);

export const saveViewsSuccess = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.CREATE_GANTT_VIEW_REQUEST,
      actionTypes.CREATE_GANTT_VIEW_FAILURE,
      actionTypes.UPDATE_GANTT_VIEWS_REQUEST,
      actionTypes.UPDATE_GANTT_VIEWS_FAILURE,
      RESET_NOTIFICATION,
    )]: () => false,
    [combineActions(
      actionTypes.CREATE_GANTT_VIEW_SUCCESS,
      actionTypes.UPDATE_GANTT_VIEWS_SUCCESS,
    )]: () => true,
  },
  false,
);

export const adjacentBlock = handleActions(
  {
    [actionTypes.SET_ADJACENT_BLOCK]: (state, { payload }) => payload,
  },
  null,
);

export const allFSEsList = handleActions(
  {
    [actionTypes.FETCH_BOARD_DATA_SUCCESS]: (state, { payload }) =>
      // Fix dubbel fetchBoardData -> MW does not sending index prop
      payload.manualSortOptions?.map(i => ({
        ...i,
        index: i?.index || i.sortIndex,
      })),
    [actionTypes.SET_ALL_TECHNICIANS]: (state, { payload }) =>
      payload,
    [combineActions(
      actionTypes.FETCH_BOARD_DATA,
      actionTypes.FETCH_BOARD_DATA_FAILURE,
    )]: state =>
        state || [],
  },
  [],
);

export const defaultSorting = handleActions(
  {
    [actionTypes.FETCH_BOARD_METADATA_SUCCESS]: (state, { payload }) =>
      sortCOPSModelToParam(payload.defaultSorting),
  },
  fseDefaultAutoSort,
);

export const rowDefinitions = handleActions(
  {
    [actionTypes.FETCH_BOARD_METADATA_SUCCESS]: (state, { payload }) =>
      payload.columnDefinitions,
  },
  [],
);

export const refetchLaneData = handleActions(
  {
    [actionTypes.SHOULD_FETCH_LANE_DATA]: (state, { payload }) =>
      payload,
  },
  false,
);

export const forceFetchGanttData = handleActions(
  {
    [actionTypes.SHOULD_FORCE_FETCH_GANTT_DATA]: (state, { payload }) => payload,
  },
  false,
);

export const ganttLaneToFetch = handleActions(
  {
    [actionTypes.FORWARD_PLANNING_PLANNED]: (state, { payload }) => payload?.laneId &&
      !state?.includes(payload?.laneId) ?
      [...state || [], payload?.laneId] : state || [],
    [combineActions(
      actionTypes.RESET_GANTT_LANE_TO_FETCH,
      'LOCATION_CHANGE',
    )]: () => null,
  },
  null,
);

export const recommendedEngineersListIsLoading = handleActions(
  {
    [combineActions(
      actionTypes.FETCH_RECOMMENDED_ENGINEERS_SUCCESS,
      actionTypes.FETCH_RECOMMENDED_ENGINEERS_FAILURE,
      'LOCATION_CHANGE',
    )]: () => false,
    [actionTypes.FETCH_RECOMMENDED_ENGINEERS]: () => true,
  },
  false,
);

export const deletedPlanningFromSockets = handleActions(
  {
    [actionTypes.DELETE_BLOCK_SUCCESS]: (state, { payload }) => payload.externalUpdate ?
      [...state, payload.block.id] : state,
    [actionTypes.RESET_SOCKET_STATE]: (state, { payload }) =>
      state.filter(s => !payload.find(p => p === s)),
    [PLANNING_UPDATE_ORDER_SOCKET_EVENTS_SUCCESS]: (state, { payload }) =>
      payload.addToDeletedArray ?
        [...(state || []).filter(s => s !== payload.id), payload.id] :
        state || [],
  },
  [],
);

export const updatedPlanningFromSockets = handleActions(
  {
    [combineActions(
      actionTypes.UPDATE_BLOCK_DATA_SUCCESS,
      actionTypes.UPDATE_BLOCK_OTHER_LANE_DATA_SUCCESS,
    )]: (state, { payload }) => payload.externalUpdate ?
      [...state, payload.block.id] : state,
    [actionTypes.RESET_SOCKET_STATE]: (state, { payload }) =>
      state.filter(s => !payload.find(p => p === s)),
    [PLANNING_UPDATE_ORDER_SOCKET_EVENTS_SUCCESS]: (state, { payload }) =>
      payload.addToUpdatedArray ?
        [...(state || []).filter(s => s !== payload.id), payload.id] :
        state || [],
  },
  [],
);

export default combineReducers({
  lanesInfo,
  lanesData,
  partiallyExecuted,
  ganttDimensions,
  viewDimensions,
  view,
  dateReference,
  boardData,
  viewData,
  isLoadingViewData,
  isLoadingBoardData,
  error,
  activeFilters,
  filterDefinitions,
  totalDataCount,
  currentPage,
  block,
  isLoadingBlockData,
  isDoingAction,
  isDraggingOver,
  saveSuccess,
  addSuccess,
  amountPlanningsToPublish,
  amountPlanningsWithPublishingError,
  planningToPublishMessage,
  planningAndViewToOpen,
  addedBlockIds,
  publishAllSuccessMessage,
  activeSort,
  activeView,
  views,
  isLoadingViews,
  isSavingViews,
  saveViewsSuccess,
  isLoadingSidepanelBlockData,
  planningsToPublish,
  isLoadingPublishButton,
  adjacentBlock,
  allFSEsList,
  defaultSorting,
  rowDefinitions,
  refetchLaneData,
  forceFetchGanttData,
  ganttLaneToFetch,
  recommendedEngineersListIsLoading,
  deletedPlanningFromSockets,
  updatedPlanningFromSockets,
});
